import { useMemo } from "react";
import { Alert, Chip, Stack } from "@mui/material";
import { SmartToyOutlined } from "@mui/icons-material";

import { useUIStore } from "../stores/UIStore";
import {
  useMessageProvenance,
  useProvenanceDialogueId,
} from "../services/ProvenanceService";
import { collectContributingAgents } from "../rehype/subAgentSections";

type SubAgentContributionStripProps = {
  /** The assistant message id this strip describes. */
  messageId?: string | null;
};

/**
 * Sub-agent attribution strip at the top of an assistant message (#20423 WS6
 * level a). Two mutually-exclusive styles from one component:
 *
 * - Untraced notice (takes precedence): when `degradation.subagent_untraced`
 *   is set, a warning banner rendering the backend copy VERBATIM.
 * - Contributor chips: one chip per distinct sub-agent that contributed
 *   (any searched query or retrieval-event ref carrying an agent). Clicking a
 *   chip opens the Sources tab, same as ResponseSourcesSummary's View Sources.
 *
 * Shares ResponseSourcesSummary's message-provenance query (same React Query
 * key), so no extra fetch. Renders nothing when only the primary assistant
 * retrieved — the common case.
 */
const SubAgentContributionStrip = ({
  messageId,
}: SubAgentContributionStripProps) => {
  const dialogueId = useProvenanceDialogueId();
  const setRightSidebar = useUIStore((s) => s.setRightSidebar);

  const { data: provenance } = useMessageProvenance(dialogueId, messageId);

  const agents = useMemo(
    () => collectContributingAgents(provenance),
    [provenance],
  );
  const untracedNotice = provenance?.degradation?.subagent_untraced ?? null;

  if (untracedNotice) {
    return (
      <Alert
        severity="warning"
        icon={<SmartToyOutlined fontSize="inherit" />}
        data-testid="aa-subagent-strip-untraced"
        sx={{ mb: 1, py: 0, alignItems: "center", borderRadius: 1 }}
      >
        {untracedNotice}
      </Alert>
    );
  }

  if (agents.length === 0) return null;

  const openSources = () =>
    setRightSidebar({
      selectedItem: "sources",
      isOpen: true,
      isArtifactItemOpen: false,
      currentArtifactId: null,
    });

  return (
    <Stack
      direction="row"
      spacing={1}
      data-testid="aa-subagent-strip"
      sx={{ mb: 1, flexWrap: "wrap", rowGap: 0.5 }}
    >
      {agents.map((agent) => (
        <Chip
          key={agent}
          size="small"
          variant="outlined"
          icon={<SmartToyOutlined />}
          label={`${agent} contributed to this response`}
          onClick={openSources}
          data-attr="assistant:chip:subagent-contribution"
        />
      ))}
    </Stack>
  );
};

export default SubAgentContributionStrip;
